import React, { useState, useCallback } from 'react';
import TopAppBar, {
  TopAppBarFixedAdjust,
  TopAppBarIcon,
  TopAppBarRow,
  TopAppBarSection,
  TopAppBarTitle,
} from '@fv-components/top-app-bar';
import MaterialIcon from '@fv-components/material-icon';
import Drawer, {
  DrawerHeader,
  DrawerTitle,
  DrawerContent,
  DrawerAppContent,
} from '@fv-components/drawer';
import {
  ApolloProvider, ApolloClient, InMemoryCache, createHttpLink,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import {
  Switch, Route, BrowserRouter, Redirect,
} from 'react-router-dom';
import { ILogin, IOrganization, IPermissionMap } from '@models';
import useConfirmationDialog from '@hooks/useConfirmationDialog';
import useCustomDialog from '@hooks/useCustomDialog';
import useSnackBar from '@hooks/useSnackBar';

import TimelyLogo from './TimelyLogo';
import Menu from './Menu/Menu';
import Admin from './Admin/Admin';
import Calculator from './Calculator/Calculator';
import AccountMenu from './AccountMenu/AccountMenu';
import NotLoggedIn from './NotLoggedIn/NotLoggedIn';
import Account from './Account/Account';
import Projects from './Projects/Projects';
import NoRoles from './NoRoles/NoRoles';
import TermsOfService from './TermsOfService/TermsOfService';
import LoggedInContentContainer from './LoggedInContentContainer/LoggedInContentContainer';
import UserManager from './UserManager/UserManager';
import Pendo from './Pendo';
import RefreshNotice from './RefreshNotice';
import SigninCallback from './Auth/SigninCallback';

const css = require('./App.module.scss');

const LOGIN_KEY = 'login';
const LEGACY_REFRESH_TOKEN_KEY = 'refreshToken';
const TOKEN_KEY = 'token';
const ACCESS_TOKEN_REFRESH_BUFFER_SECONDS = 15;
const REVOKE_REFRESH_TOKEN_TIMEOUT_MS = 5000;

interface IRevokeRefreshTokenResponse {
  data?: {
    revokeRefreshToken?: boolean;
  };
}

// Share one refresh request across concurrent 401s so refresh-token rotation does not race itself.
let refreshLoginPromise: Promise<ILogin | null> | null = null;
// Increment this whenever the current login is intentionally invalidated. In-flight
// refreshes compare against the generation they started in before writing new tokens.
let activeAuthGeneration = 0;
let isLoggingOut = false;

const isObjectRecord = (value: unknown): value is Record<string, unknown> => (
  value !== null && typeof value === 'object'
);

const isValidPermissions = (value: unknown): boolean => {
  if (value === undefined) {
    return true;
  }

  if (typeof value !== 'string') {
    return false;
  }

  try {
    const parsedPermissions = JSON.parse(value);
    return parsedPermissions === null || isObjectRecord(parsedPermissions);
  } catch {
    return false;
  }
};

const getRefreshTokenLogin = (value: unknown): ILogin | null => {
  if (!isObjectRecord(value) || !isObjectRecord(value.data)) {
    return null;
  }

  const { refreshToken } = value.data;
  if (!isObjectRecord(refreshToken)) {
    return null;
  }

  if (typeof refreshToken.token !== 'string' || typeof refreshToken.refreshToken !== 'string') {
    return null;
  }

  if (!isValidPermissions(refreshToken.permissions)) {
    return null;
  }

  return refreshToken;
};

const clearStoredLogin = () => {
  localStorage.removeItem(LOGIN_KEY);
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(LEGACY_REFRESH_TOKEN_KEY);
};

const getLoginFromLocalStorage = (requireRefreshSource = false): ILogin | undefined => {
  const loginObjectString = localStorage.getItem(LOGIN_KEY);
  if (!loginObjectString) {
    return undefined;
  }
  let login: ILogin;
  try {
    login = JSON.parse(loginObjectString) as ILogin;
  } catch {
    clearStoredLogin();
    return undefined;
  }
  if (!login || typeof login !== 'object') {
    clearStoredLogin();
    return undefined;
  }
  const legacyRefreshToken = localStorage.getItem(LEGACY_REFRESH_TOKEN_KEY);
  if (legacyRefreshToken && !login.refreshToken) {
    login = {
      ...login,
      refreshToken: legacyRefreshToken,
    };
    localStorage.setItem(LOGIN_KEY, JSON.stringify(login));
    localStorage.removeItem(LEGACY_REFRESH_TOKEN_KEY);
  }
  if (requireRefreshSource && !login.refreshToken) {
    clearStoredLogin();
    return undefined;
  }
  return login;
};

const getLoginWithPermissionMap = (login: ILogin): ILogin => ({
  ...login,
  permissionMap: login.permissionMap || JSON.parse(login.permissions || '{}'),
});

const setLoginInLocalStorage = (login: ILogin) => {
  const loginWithPermissionMap = getLoginWithPermissionMap({
    ...(getLoginFromLocalStorage() || {}),
    ...login,
  });

  // Residual risk: refresh tokens still arrive in GraphQL until backend cookie support is added.
  localStorage.removeItem(LEGACY_REFRESH_TOKEN_KEY);

  if (loginWithPermissionMap.token) {
    localStorage.setItem(TOKEN_KEY, loginWithPermissionMap.token);
  }
  localStorage.setItem(LOGIN_KEY, JSON.stringify(loginWithPermissionMap));
};

const redirectToLogin = () => {
  localStorage.clear();
  window.location.href = '/';
};

const getAccessTokenExpiration = (token: string): number | null => {
  const payload = token.split('.')[1];
  if (!payload) {
    return null;
  }

  try {
    const normalizedPayload = payload
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    const paddedPayload = normalizedPayload.padEnd(
      normalizedPayload.length + ((4 - (normalizedPayload.length % 4)) % 4),
      '=',
    );
    const decodedPayload = atob(paddedPayload);
    const parsedPayload = JSON.parse(decodedPayload) as { exp?: number };
    return parsedPayload.exp || null;
  } catch {
    return null;
  }
};

const isAccessTokenExpiring = (token: string, bufferSeconds = 0): boolean => {
  const expiration = getAccessTokenExpiration(token);
  if (!expiration) {
    return false;
  }
  return expiration <= Math.floor(Date.now() / 1000) + bufferSeconds;
};

const getLoginIfRefreshTokenChanged = (
  refreshToken: string,
  refreshRequestAuthGeneration = activeAuthGeneration,
): ILogin | null => {
  if (isLoggingOut || refreshRequestAuthGeneration !== activeAuthGeneration) {
    return null;
  }

  const currentLogin = getLoginFromLocalStorage();
  const currentRefreshToken = currentLogin?.refreshToken;

  if (currentRefreshToken && currentRefreshToken !== refreshToken && currentLogin?.token) {
    return getLoginWithPermissionMap(currentLogin);
  }

  return null;
};

const fetchRefreshedLogin = async (): Promise<ILogin | null> => {
  if (isLoggingOut) {
    return null;
  }

  // Capture the auth generation before awaiting the refresh request. If logout
  // happens while this request is in flight, the generation changes and this
  // response is ignored instead of restoring a logged-out session.
  const refreshRequestAuthGeneration = activeAuthGeneration;
  const refreshToken = getLoginFromLocalStorage(true)?.refreshToken;
  if (!refreshToken) {
    return null;
  }

  try {
    const response = await fetch('/graphql', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query: `
          mutation RefreshToken($refreshToken: String!) {
            refreshToken(refreshToken: $refreshToken) {
              name
              email
              token
              refreshToken
              permissions
              userId
              organizationId
              organizationName
            }
          }
        `,
        variables: { refreshToken },
      }),
    });

    if (!response.ok) {
      return getLoginIfRefreshTokenChanged(refreshToken, refreshRequestAuthGeneration);
    }

    const result: unknown = await response.json();
    const login = getRefreshTokenLogin(result);
    if (!login) {
      return getLoginIfRefreshTokenChanged(refreshToken, refreshRequestAuthGeneration);
    }

    if (isLoggingOut || refreshRequestAuthGeneration !== activeAuthGeneration) {
      return null;
    }

    const changedLogin = getLoginIfRefreshTokenChanged(refreshToken, refreshRequestAuthGeneration);
    if (changedLogin) {
      return changedLogin;
    }

    setLoginInLocalStorage(login);
    return login;
  } catch {
    return getLoginIfRefreshTokenChanged(refreshToken, refreshRequestAuthGeneration);
  }
};

const revokeRefreshToken = async (refreshToken?: string): Promise<boolean> => {
  if (!refreshToken) {
    return false;
  }

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), REVOKE_REFRESH_TOKEN_TIMEOUT_MS);

    try {
      const response = await fetch('/graphql', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        signal: controller.signal,
        body: JSON.stringify({
          query: `
            mutation RevokeRefreshToken($refreshToken: String!) {
              revokeRefreshToken(refreshToken: $refreshToken)
            }
          `,
          variables: { refreshToken },
        }),
      });

      if (!response.ok) {
        return false;
      }

      const result = await response.json() as IRevokeRefreshTokenResponse;
      return !!result.data?.revokeRefreshToken;
    } finally {
      clearTimeout(timeoutId);
    }
  } catch {
    return false;
  }
};

const refreshLogin = (): Promise<ILogin | null> => {
  if (!refreshLoginPromise) {
    refreshLoginPromise = fetchRefreshedLogin()
      .finally(() => {
        refreshLoginPromise = null;
      });
  }
  return refreshLoginPromise;
};

const retryWithToken = (input: RequestInfo, init: RequestInit | undefined, token: string) => {
  const headers = new Headers(init?.headers);
  headers.set('Authorization', `Bearer ${token}`);
  return fetch(input, {
    ...init,
    headers,
  });
};

const getAccessToken = async (): Promise<string | null> => {
  if (isLoggingOut) {
    return null;
  }

  if (!getLoginFromLocalStorage(true)) {
    clearStoredLogin();
    return null;
  }

  const token = localStorage.getItem(TOKEN_KEY);
  if (!token) {
    return null;
  }

  if (!isAccessTokenExpiring(token, ACCESS_TOKEN_REFRESH_BUFFER_SECONDS)) {
    return token;
  }

  const refreshedLogin = await refreshLogin();
  if (refreshedLogin?.token) {
    return refreshedLogin.token;
  }

  if (isAccessTokenExpiring(token)) {
    redirectToLogin();
    return null;
  }

  return token;
};

const httpLink = createHttpLink({
  uri: '/graphql',
  fetch: async (input: RequestInfo, init?: RequestInit): Promise<Response> => {
    const response = await fetch(input, init);

    if (response.status !== 401) {
      return response;
    }

    const refreshedLogin = await refreshLogin();
    if (!refreshedLogin?.token) {
      redirectToLogin();
      return response;
    }

    const retryResponse = await retryWithToken(input, init, refreshedLogin.token);
    if (retryResponse.status === 401) {
      redirectToLogin();
    }
    return retryResponse;
  },
});

const authLink = setContext(async (_, { headers }) => {
  // get the authentication token from local storage if it exists
  const token = await getAccessToken();
  // return the headers to the context so httpLink can read them
  return {
    headers: {
      ...headers,
      Authorization: token ? `Bearer ${token}` : null,
    },
  };
});

export const timelyClient = new ApolloClient({
  cache: new InMemoryCache(),
  link: authLink.concat(httpLink),
});

interface IAppState {
  userFullName?: string;
  userId?: string;
  permissions?: IPermissionMap;
  email?: string;
  organization?: IOrganization;
  organizationName?: string;
  canUseCalculator?: boolean;
  canUseUserManager?: boolean;
}

const loginToAppState = (login: ILogin): IAppState => ({
  userFullName: login.name,
  userId: login.userId,
  permissions: login.permissionMap,
  email: login.email,
  organization: { id: login.organizationId, description: login.organizationName },
  canUseCalculator: !!login.permissionMap?.Calculation?.Read
      || !!login.permissionMap?.Calculation?.ReadLimited,
  canUseUserManager: !!login.organizationId
      && (!!login.permissionMap?.User?.ReadLimited || !!login.permissionMap?.User?.Read)
      && (!!login.permissionMap?.Invite?.ReadLimited || !!login.permissionMap?.Invite?.Read)
      && (!!login.permissionMap?.Invite?.Write || !!login.permissionMap?.Invite?.WriteLimited),
});

const getAppStateFromLocalStorage = () : IAppState => {
  const login = getLoginFromLocalStorage(true);
  if (login?.token) {
    return loginToAppState(getLoginWithPermissionMap(login));
  }
  return {};
};

const App: React.FC = () => {
  const [state, setState] = useState<IAppState>(getAppStateFromLocalStorage());
  // For reasons beyond my understanding you cannot combine the isMenuVisible state
  // with the rest of the state or it will lose all of its state when the menu closes
  const [isMenuVisible, setIsMenuVisible] = useState(false);
  const { ConfirmationDialog } = useConfirmationDialog();
  const { CustomDialog } = useCustomDialog();
  const { SnackBar } = useSnackBar();

  const onLoginSuccess = useCallback((login: ILogin) => {
    if (login.token) {
      setLoginInLocalStorage(login);
      setState(loginToAppState(getLoginWithPermissionMap(login)));
    }
  }, []);

  const logOut = async () => {
    const refreshToken = getLoginFromLocalStorage()?.refreshToken;
    // Mark the current auth session as stale before clearing storage so any
    // refresh request already in flight cannot write a new login afterward.
    activeAuthGeneration += 1;
    isLoggingOut = true;

    try {
      await revokeRefreshToken(refreshToken);
    } finally {
      try {
        refreshLoginPromise = null;
        setState({ permissions: undefined });
        localStorage.clear();
        await timelyClient.clearStore();
      } finally {
        isLoggingOut = false;
      }
    }
  };

  const showMenu = useCallback((show: boolean) => {
    setIsMenuVisible(show);
  }, []);

  const onAccountUpdate = (
    firstName: string,
    lastName: string,
    email: string,
    token: string,
  ) => {
    setState({
      ...state,
      userFullName: `${firstName} ${lastName}`,
      email,
    });
    const login: ILogin = getLoginFromLocalStorage() || {};
    setLoginInLocalStorage({
      ...login,
      name: `${firstName} ${lastName}`,
      email,
      token,
    });
  };

  return (
    <>
      <BrowserRouter>
        <ApolloProvider client={timelyClient}>
          {!state.userId
            ? (
              <NotLoggedIn
                onLoginSuccess={onLoginSuccess}
              />
            ) : (
              <>
                <div className={css.printLogo}>
                  <TimelyLogo isBlack={false} width={96} />
                </div>
                <TopAppBar className={css.appBar}>
                  <TopAppBarRow>
                    <TopAppBarSection align="start">
                      <TopAppBarIcon navIcon tabIndex={0}>
                        <MaterialIcon
                          hasRipple
                          icon="menu"
                          onClick={() => showMenu(!isMenuVisible)}
                          aria-label="Menu"
                          data-cy="menu"
                        />
                      </TopAppBarIcon>
                      <TopAppBarTitle>
                        <TimelyLogo isBlack={false} width={96} />
                      </TopAppBarTitle>
                    </TopAppBarSection>
                    <TopAppBarSection align="end" role="toolbar">
                      <TopAppBarIcon actionItem tabIndex={0}>
                        <AccountMenu
                          userFullName={state.userFullName}
                          userId={state.userId}
                          onLogoutClicked={logOut}
                          permissions={state.permissions}
                        />
                      </TopAppBarIcon>
                    </TopAppBarSection>
                  </TopAppBarRow>
                </TopAppBar>
                <TopAppBarFixedAdjust>
                  <Drawer
                    modal
                    open={isMenuVisible}
                    onClose={() => showMenu(false)}
                    data-cy="menu-container"
                  >
                    {
              state.userFullName && (
              <DrawerHeader>
                <DrawerTitle tag="h2" data-cy="active-full-user-name">{state.userFullName}</DrawerTitle>
              </DrawerHeader>
              )
            }
                    <DrawerContent>
                      <Menu
                        showMenu={showMenu}
                        permissions={state.permissions}
                        organizationId={state.organization?.id}
                      />
                    </DrawerContent>
                  </Drawer>
                  <DrawerAppContent>
                    <Switch>
                      {state.permissions && (
                      <Route
                        path="/admin"
                      >
                        <Admin permissions={state.permissions} />
                      </Route>
                      )}
                      <Route
                        exact
                        path="/my-profile/:userId"
                      >
                        <Account onAccountUpdate={onAccountUpdate} loggedInUserId={state.userId} />
                      </Route>
                      {!!state.permissions?.History?.ReadLimited && (
                        <Route
                          exact
                          path="/projects/:userId"
                        >
                          <Projects timelyClient={timelyClient} loggedInUserId={state.userId} />
                        </Route>
                      )}
                      <Route
                        exact
                        path="/terms"
                        render={() => (
                          <LoggedInContentContainer>
                            <TermsOfService />
                          </LoggedInContentContainer>
                        )}
                      />
                      <Route
                        exact
                        path="/signin"
                      >
                        <SigninCallback />
                      </Route>
                      <Route
                        exact
                        path="/users"
                        render={() => {
                          if (state.canUseUserManager) {
                            return (
                              <UserManager
                                organization={state.organization}
                              />
                            );
                          }
                          return <Redirect to="/calculator" />;
                        }}
                      />
                      <Route
                        exact
                        path={['calculator/deadlines/', '/calculator/new', '/calculator/deadlines/:historyId', '/calculator/:historyId', '/calculator']}
                        render={() => {
                          if (state.canUseCalculator) {
                            return (
                              <Calculator />
                            );
                          }
                          return <NoRoles />;
                        }}
                      />
                      {/* If they have some crazy path I'm going to redirect back to / */}
                      <Route>
                        <Redirect
                          to="/calculator"
                        />
                      </Route>
                    </Switch>
                  </DrawerAppContent>
                </TopAppBarFixedAdjust>
              </>
            )}
        </ApolloProvider>
      </BrowserRouter>
      <ConfirmationDialog />
      <CustomDialog />
      <SnackBar />
      <RefreshNotice />
      <Pendo
        userId={state.userId}
        email={state.email}
        organizationId={state.organization?.id}
        organizationName={state.organizationName}
        userFullName={state.userFullName}
      />
    </>
  );
};

export default App;
